import { ReactElement, useContext, useState } from "react";
import InputDialog from "../../../../../components/dialogs/inputDialog";
import TextField from "@mui/material/TextField";
import {
  CheckValueType,
  IField10Type,
  IField12Type, IField13Type, IField14Type,
  IField9Type,
  IShiftReportContent,
  IShiftReportEdited,
  IShiftReportRM
} from "./types";
import { cloneObject } from "../../../../../system/utils";
import { SystemContext } from "../../../../../context/systemContext";
import RowComponent from "../../../components/rowComponent";
import ColComponent from "../../../components/colComponent";
import MaintenanceSAPPanel from "./maintenanceSAPPanel";
import { ISystemContextData } from "../../../../../types";
import RmCheckList from "./rmCheckList";
import HSSPanel from "./hssPanel";

interface IProps {
  reportData: IShiftReportRM;
  onAccept: (report: IShiftReportEdited) => void;
  onClose: () => void;
}


interface IFieldConfig<T> {
  field: T;
  title: string;
  subTitle?: string;

}


const fieldsA: IFieldConfig<keyof IShiftReportContent>[] = [
  {
    field: "field1",
    title: "Safety/Environmental Events",
    subTitle: "Incidents, positive, share, NM, causes or suspicious causes, Immediate action taken, accountability applied,…"
  },
  {
    field: "field2",
    title: "Quality Events",
    subTitle: "Hold and Scrap tons, causes or suspicious causes, Immediate action taken, accountability applied,…"
  },
  {
    field: "field3",
    title: "Operational Events",
    subTitle: "Incidents, Call offs, Accomplishments, Shift change opportunities, Improvement opportunities identified, causes or suspicious causes, Immediate action taken, accountability applied,…"
  },
  {
    field: "field4",
    title: "Maintenance Events",
    subTitle: "Incidents, delays, Response time opportunities, causes or suspicious causes, Immediate action taken, accountability applied,…"
  },
  {
    field: "maintenanceSAP",
    title: "",
    subTitle: ""
  },
  {
    field: "field5",
    title: "All unscheduled delays have detailed description in QMOS?"
  },
  {
    field: "field6",
    title: "Electrical shift report verified completed / issues?"
  },
  {
    field: "field7",
    title: "North End Mech shift report verified completed / issues?"
  },
  {
    field: "field8",
    title: "South End Mech shift report verified completed / issues?"
  },

];

const fieldsB: IFieldConfig<keyof IField9Type>[] = [
  {
    field: "begin",
    title: "Beginning of shift",
    subTitle: "within first 4 hours"
  },
  {
    field: "middle",
    title: "Middle of shift",
    subTitle: "within 2 hours of lunch"
  }
];

const fieldsC: IFieldConfig<keyof IField10Type>[] = [
  {
    field: "f1Crane",
    title: "F1 crane"
  },
  {
    field: "ghCrane",
    title: "GH crane"
  },
  {
    field: "f2Crane",
    title: "F2 Crane"
  },
  {
    field: "chsCrane",
    title: "CHS Crane"
  }
];



const processData = (report: IShiftReportRM, systemContext: ISystemContextData): IShiftReportRM => {
  const newObject = cloneObject(report);

  if (!newObject.user && systemContext.user) {
    const user =  systemContext.user;

    newObject.user = {
      userID: user.userID,
      userName: user.fullName,
      email: user.email
    };
  }

  if (!newObject.content) {
    newObject.content = {
      field1: "",
      field2: "",
      field3: "",
      field4: "",
      field5: "",
      field6: "",
      field7: "",
      field8: "",
      field9: {
        begin: "",
        middle: ""
      },
      field10: {
        f1Crane: "",
        ghCrane: "",
        f2Crane: "",
        chsCrane: "",
      },
      field11: "",
      billetsRolled: 0,
      field12: {
        nose: 0,
        tail: 0,
        lost: 0
      },
      field13: {
        nose: 0,
        lost: 0
      },
      field14: [{
        nose: 0,
        tail: 0,
        product: {
          name: "NONE",
          value: 0
        },
        lost: 0
      }],
      maintenanceSAP: { items: [] },
      rmCheckList: {
        guBurnInIntermediateMillCheckList_8_15_2023: CheckValueType.NONE,
        checkListAfterCobbles: CheckValueType.NONE,
        checkListPassChangeNtm_rev_2: CheckValueType.NONE,
        checkListWithBarInTheMill_2024: CheckValueType.NONE,
        intermediateMillChecklist_2024: CheckValueType.NONE,
        mainPulpitCheckSheet_rev2: CheckValueType.NONE,
        startupChecks: CheckValueType.NONE,
      }
    };
  } else {
    if (!Array.isArray(newObject.content.field14)) {
      const value = newObject.content.field14 as IField14Type;
      newObject.content.field14 = [{
        nose: Number(value.nose) ?? 0,
        tail: Number(value.tail) ?? 0,
        product: value.product ?? {
          name: "NONE",
          value: 0
        },
        lost: value.lost ?? 0
      }];
    }

    newObject.content.billetsRolled = newObject.content.billetsRolled ?? 0;

    newObject.content.field12 = {
      nose: Number(newObject.content.field12?.nose) ?? 0,
      tail: Number(newObject.content.field12?.tail) ?? 0,
      lost: Number(newObject.content.field12?.lost) ?? 0
    };

    newObject.content.field13 = {
      nose: Number(newObject.content.field13?.nose) ?? 0,
      lost: Number(newObject.content.field13?.lost) ?? 0
    };
  }

  return newObject;
};


const ShiftReportEditor = ({ onClose, reportData, onAccept }: IProps): ReactElement => {
  const systemContext  = useContext(SystemContext)!;
  const [ editReport, setEditReport ] = useState<IShiftReportRM>(processData(reportData, systemContext));
  const [ edited, setEdited ] = useState(false);

  const onInternalAccept = () => {
    onAccept({
      shiftReportID: editReport.shiftReportID,
      areaID: editReport.area.areaID,
      zoneID: null,
      userID: systemContext.user!.userID,
      shiftHistoryID: editReport.shift.shiftHistoryID,
      content: editReport.content!
    });
  };

  const onChangeFieldA = (field: keyof IShiftReportContent, value: any) => {
    const newValue = { ...editReport };

    if (newValue.content) {
      (newValue.content[field] as any) = value;
      setEditReport(newValue);
      setEdited(true);
    }
  };

  const onChangeFieldB = (field: keyof IField9Type, value: any) => {
    const newValue = { ...editReport };

    if (newValue.content) {
      newValue.content.field9[field] = value;
      setEditReport(newValue);
      setEdited(true);
    }
  };

  const onChangeFieldC = (field: keyof IField10Type, value: any) => {
    const newValue = { ...editReport };

    if (newValue.content) {
      newValue.content.field10[field] = value;
      setEditReport(newValue);
      setEdited(true);
    }
  };

  const onChangeField12 = (field: keyof IField12Type, value: string) => {
    const newValue = { ...editReport };

    if (newValue.content) {
      if (!newValue.content.field12) {
        newValue.content.field12 = {
          nose: 0,
          tail: 0
        };
      }

      newValue.content.field12[field] = Number(value);
      setEditReport(newValue);
      setEdited(true);
    }
  };

  const onChangeField13 = (field: keyof IField13Type, value: string) => {
    const newValue = { ...editReport };

    if (newValue.content) {
      if (!newValue.content.field13) {
        newValue.content.field13 = {
          nose: 0
        };
      }

      newValue.content.field13[field] = Number(value);
      setEditReport(newValue);
      setEdited(true);
    }
  };

  const onChangeField14 = (_property: keyof IShiftReportContent, value: IField14Type[]) => {
    const newValue = { ...editReport };

    if (newValue.content) {
      // if (!newValue.content.field14) {
      //   newValue.content.field14 = {
      //     nose: 0,
      //     tail: 0
      //   };
      // }

      newValue.content.field14 = value;
      setEditReport(newValue);
      setEdited(true);
    }
  };



  return (
    <InputDialog onAccept={onInternalAccept} onCancel={onClose} title="Shift Report" open={true} canAccept={edited} maxWidth="lg">
      <div className="display-flex-column gap-1" >
        {fieldsA.map(field => {
          return field.field !== "maintenanceSAP"
            ? <RowComponent key={`row-${field.field}`} title={field.title} subTitle={field.subTitle} >
              <TextField
                variant="outlined"
                placeholder="to be filled..."
                multiline={true}
                minRows={4}
                maxRows={4}
                value = {editReport.content ? editReport.content[field.field] : ""}
                fullWidth={true}
                onChange={event => onChangeFieldA(field.field, event.target.value)}
              />
            </RowComponent>
            : <MaintenanceSAPPanel key={`row-${field.field}`}  value={editReport.content?.maintenanceSAP} property="maintenanceSAP" onChange={onChangeFieldA}/>;
        })}
        <RowComponent title="Compactor Cleaning completed properly? verified completed /any issues?" subTitle="Add timestamp that it was completed">
          <div className="display-flex-row gap-1 w-100">
            {fieldsB.map(field => {
              return (
                <ColComponent key={`col-field9-${field.field}`} title={field.title} subTitle={field.subTitle} >
                  <TextField
                    variant="outlined"
                    placeholder="to be filled..."
                    multiline={true}
                    minRows={4}
                    maxRows={4}
                    value = {editReport.content ? editReport.content.field9[field.field] : ""}
                    fullWidth={true}
                    onChange={event => onChangeFieldB(field.field, event.target.value)}
                  />
                </ColComponent>
              );
            })}
          </div>
        </RowComponent>

        <RowComponent title="Crane Pre Use Inspection verified completed / issues?" >
          <div className="display-flex-row gap-1">
            {fieldsC.map(field => {
              return (
                <ColComponent key={`col-field10-${field.field}`} title={field.title} subTitle={field.subTitle} >
                  <TextField
                    variant="outlined"
                    placeholder="to be filled..."
                    multiline={true}
                    minRows={4}
                    maxRows={4}
                    value = {editReport.content ? editReport.content.field10[field.field] : ""}
                    fullWidth={true}
                    onChange={event => onChangeFieldC(field.field, event.target.value)}
                  />
                </ColComponent>
              );
            })}
          </div>
        </RowComponent>

        <RowComponent title="How many non-full billet lengths [E.G. HALF, QUARTER ETC]  rolled in the mill and Why?" >
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {editReport.content ? editReport.content.field11 ?? "" : ""}
            fullWidth={true}
            onChange={event => onChangeFieldA("field11", event.target.value)}
          />
        </RowComponent>

        <RowComponent title="Billets Rolled" >
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {editReport.content ? editReport.content.billetsRolled ?? 0 : 0}
            fullWidth={true}
            onChange={event => onChangeFieldA("billetsRolled", event.target.value)}
          />
        </RowComponent>

        <RowComponent title="C&C" >
          <div className="display-flex-row gap-1 w-100">
            <ColComponent title="Nose crop length" >
              <TextField
                variant="outlined"
                placeholder="to be filled..."
                type="number"
                value = {editReport.content && editReport.content.field12 ? editReport.content.field12.nose : 0}
                fullWidth={true}
                onChange={event => onChangeField12("nose", event.target.value)}
              />
            </ColComponent>
            <ColComponent title="Tail crop length" >
              <TextField
                variant="outlined"
                placeholder="to be filled..."
                type="number"
                value = {editReport.content && editReport.content.field12 ? editReport.content.field12.tail : 0}
                fullWidth={true}
                onChange={event => onChangeField12("tail", event.target.value)}
              />
            </ColComponent>
          </div>
        </RowComponent>

        <RowComponent title="C&D" >
          <div className="display-grid gap-1 w-100" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <ColComponent title="Nose crop length" >
              <TextField
                variant="outlined"
                placeholder="to be filled..."
                type="number"
                value = {editReport.content && editReport.content.field13 ? editReport.content.field13.nose : 0}
                fullWidth={true}
                onChange={event => onChangeField13("nose", event.target.value)}
              />
            </ColComponent>
            <div className="flex-grow-1" />
          </div>
        </RowComponent>

        <RowComponent title="HSS" >
          <HSSPanel value={editReport.content?.field14 as IField14Type[]} property="field14" onChange={onChangeField14}/>
        </RowComponent>

        <RmCheckList value={editReport.content?.rmCheckList} property="rmCheckList" onChange={onChangeFieldA}/>

      </div>
    </InputDialog>
  );
};


export default ShiftReportEditor;